import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ExpandModal from '../components/ExpandModal';
import AddFragranceModal from '../components/AddFragranceModal';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handle = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handle);
        return () => window.removeEventListener('resize', handle);
    }, []);
    return isMobile;
};

const Discover = ({ token }) => {
    const isMobile = useIsMobile();
    const [fragrances, setFragrances]               = useState([]);
    const [recommendations, setRecommendations]     = useState([]);
    const [allRecommendations, setAllRecommendations] = useState([]);
    const [recOffset, setRecOffset]                 = useState(0);
    const [search, setSearch]                       = useState('');
    const [filters, setFilters]                     = useState({ season: '', occasion: '', main_accord: '', gender: '' });
    const [showFilters, setShowFilters]             = useState(false);
    const [loading, setLoading]                     = useState(true);
    const [detailFragrance, setDetailFragrance]     = useState(null);
    const [libraryIds, setLibraryIds]               = useState([]);
    const [wishlistIds, setWishlistIds]             = useState([]);
    const [hasNotes, setHasNotes]                   = useState(false);
    const [showRequestModal, setShowRequestModal]   = useState(false);

    const API = process.env.REACT_APP_API_URL;

    useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchAll = async () => {
        try {
            const fragRes = await axios.get(`${API}/api/fragrances`);
            setFragrances(fragRes.data);
        } catch (err) { console.error(err); }
        try {
            const recRes = await axios.get(`${API}/api/fragrances/recommendations`, { headers: { Authorization: `Bearer ${token}` } });
            setAllRecommendations(recRes.data);
            setRecommendations(recRes.data.slice(0, 3));
        } catch (err) { console.error(err); }
        try {
            const libRes = await axios.get(`${API}/api/library`, { headers: { Authorization: `Bearer ${token}` } });
            setLibraryIds(libRes.data.map(item => item.fragrance_id));
        } catch (err) { console.error(err); }
        try {
            const wlRes = await axios.get(`${API}/api/wishlist`, { headers: { Authorization: `Bearer ${token}` } });
            setWishlistIds(wlRes.data.map(item => item.fragrance_id));
        } catch (err) { console.error(err); }
        try {
            const profileRes = await axios.get(`${API}/api/profile`, { headers: { Authorization: `Bearer ${token}` } });
            const notes = profileRes.data?.profile?.favourite_notes || [];
            setHasNotes(notes.length > 0);
        } catch (err) { console.error(err); }
        setLoading(false);
    };

    const handleRefreshRecs = async () => {
        if (allRecommendations.length <= 3) return;
        const nextOffset = recOffset + 3;
        if (nextOffset >= allRecommendations.length) {
            try {
                const recRes = await axios.get(`${API}/api/fragrances/recommendations`, { headers: { Authorization: `Bearer ${token}` } });
                const fresh = recRes.data;
                setAllRecommendations(fresh);
                setRecOffset(0);
                setRecommendations(fresh.slice(0, 3));
            } catch (err) { console.error(err); }
            return;
        }
        setRecOffset(nextOffset);
        setRecommendations(allRecommendations.slice(nextOffset, nextOffset + 3));
    };

    const allAccords = [...new Set(fragrances.map(f => f.main_accord).filter(Boolean))].sort();
    const allGenders = [...new Set(fragrances.map(f => f.gender).filter(Boolean))].sort();
    const seasons    = ['Spring', 'Summer', 'Autumn', 'Winter'];
    const occasions  = ['Casual', 'Office', 'Evening', 'Date', 'Sport', 'Night Out'];

    const filtered = fragrances.filter(f => {
        const matchSearch   = !search || f.name.toLowerCase().includes(search.toLowerCase()) || f.brand.toLowerCase().includes(search.toLowerCase());
        const matchSeason   = !filters.season || (Array.isArray(f.season) && f.season.includes(filters.season));
        const matchOccasion = !filters.occasion || (Array.isArray(f.occasion) && f.occasion.includes(filters.occasion));
        const matchAccord   = !filters.main_accord || f.main_accord === filters.main_accord;
        const matchGender   = !filters.gender || f.gender === filters.gender;
        return matchSearch && matchSeason && matchOccasion && matchAccord && matchGender;
    });

    const isOwned      = (id) => libraryIds.includes(id);
    const isWishlisted = (id) => wishlistIds.includes(id);

    const handleWishlist = async (e, item) => {
        e.stopPropagation();
        e.preventDefault();
        try {
            if (isWishlisted(item.id)) {
                await axios.delete(`${API}/api/wishlist/fragrance/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
                setWishlistIds(prev => prev.filter(id => id !== item.id));
            } else {
                await axios.post(`${API}/api/wishlist`, { fragrance_id: item.id }, { headers: { Authorization: `Bearer ${token}` } });
                setWishlistIds(prev => [...prev, item.id]);
            }
        } catch (err) { console.error(err); }
    };

    const parseNotes = (notes) => {
        if (!notes) return [];
        if (Array.isArray(notes)) return notes;
        try { return JSON.parse(notes); } catch { return [notes]; }
    };

    const ratingDots = (value, max = 5) =>
        Array.from({ length: max }, (_, i) => (
            <span key={i} style={{ display: 'inline-block', width: '5px', height: '5px', borderRadius: '50%', background: i < value ? '#c9a96e' : '#2a2a2a', marginRight: '4px' }} />
        ));

    const hasFilters = search || filters.season || filters.occasion || filters.main_accord || filters.gender;
    const activeFilterCount = [filters.season, filters.occasion, filters.main_accord, filters.gender].filter(Boolean).length;

    if (loading) return (
        <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', color: '#c9a96e', fontStyle: 'italic' }}>Loading...</p>
        </div>
    );

    // ── FEATURED CARD (Curated for You) ──
    const FeaturedCard = ({ item, index }) => {
        const [hovered, setHovered] = useState(false);
        const owned      = isOwned(item.id);
        const wishlisted = isWishlisted(item.id);
        const topNotes   = parseNotes(item.top_notes).slice(0, 3);
        const baseNotes  = parseNotes(item.base_notes).slice(0, 3);

        return (
            <div
                onClick={() => setDetailFragrance(item)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    background: hovered ? '#161616' : '#0f0f0f',
                    border: '1px solid #2a2a2a',
                    borderLeft: hovered ? '1px solid #c9a96e' : '1px solid #2a2a2a',
                    padding: isMobile ? '20px 16px' : '36px 40px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex', flexDirection: 'column',
                    position: 'relative',
                }}
            >
                {!isMobile && (
                    <span style={{ position: 'absolute', top: '32px', right: '36px', fontFamily: 'Cormorant Garamond, serif', fontSize: '3rem', fontWeight: 300, color: '#1e1e1e', lineHeight: 1, userSelect: 'none' }}>0{index + 1}</span>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.35em', textTransform: 'uppercase', color: '#c9a96e' }}>{item.brand}</span>
                    {item.main_accord && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#4a4a4a', border: '1px solid #222', padding: '2px 8px', whiteSpace: 'nowrap' }}>{item.main_accord}</span>}
                </div>

                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.4rem' : '2rem', fontWeight: 300, color: '#f5f0eb', lineHeight: 1.1, marginBottom: isMobile ? '16px' : '24px' }}>{item.name}</p>

                {/* On mobile — just show price and wishlist, hide notes */}
                {isMobile ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '14px', borderTop: '1px solid #1a1a1a' }}>
                        <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 300, color: item.price_gbp ? '#c9a96e' : 'transparent' }}>{item.price_gbp ? `£${item.price_gbp}` : '—'}</span>
                        {owned ? (
                            <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: '#665e58', border: '1px solid #2a2a2a', padding: '6px 12px' }}>In Library</span>
                        ) : (
                            <button
                                onClick={e => handleWishlist(e, item)}
                                style={{ background: wishlisted ? '#c9a96e' : 'none', border: '1px solid #c9a96e', color: wishlisted ? '#0a0a0a' : '#c9a96e', fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '6px 14px', cursor: 'pointer' }}
                            >
                                {wishlisted ? '♥ Saved' : '♡ Wishlist'}
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>
                            <div>
                                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#4a4a4a', marginBottom: '8px' }}>Top Notes</p>
                                {topNotes.map((n, i) => <p key={i} style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 300, color: '#a09890', marginBottom: '3px' }}>{n}</p>)}
                            </div>
                            <div>
                                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#4a4a4a', marginBottom: '8px' }}>Base Notes</p>
                                {baseNotes.map((n, i) => <p key={i} style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 300, color: '#a09890', marginBottom: '3px' }}>{n}</p>)}
                            </div>
                            <div>
                                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#4a4a4a', marginBottom: '8px' }}>Performance</p>
                                <div style={{ marginBottom: '6px' }}>
                                    <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', color: '#665e58', marginBottom: '3px' }}>Longevity</p>
                                    <div>{ratingDots(item.longevity)}</div>
                                </div>
                                <div>
                                    <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', color: '#665e58', marginBottom: '3px' }}>Projection</p>
                                    <div>{ratingDots(item.sillage)}</div>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '20px', borderTop: '1px solid #1a1a1a' }}>
                            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontWeight: 300, color: item.price_gbp ? '#c9a96e' : 'transparent' }}>{item.price_gbp ? `£${item.price_gbp}` : '—'}</span>
                            {owned ? (
                                <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#665e58', border: '1px solid #2a2a2a', padding: '8px 18px' }}>In Library</span>
                            ) : (
                                <button
                                    onClick={e => handleWishlist(e, item)}
                                    onMouseEnter={e => { if (!wishlisted) { e.currentTarget.style.background = '#c9a96e'; e.currentTarget.style.color = '#0a0a0a'; } }}
                                    onMouseLeave={e => { if (!wishlisted) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#c9a96e'; } }}
                                    style={{ background: wishlisted ? '#c9a96e' : 'none', border: '1px solid #c9a96e', color: wishlisted ? '#0a0a0a' : '#c9a96e', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', padding: '8px 18px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                                >
                                    {wishlisted ? '♥ Wishlisted' : '♡ Save to Wishlist'}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    };

    // ── GRID CARD ──
    const FragranceCard = ({ item }) => {
        const owned      = isOwned(item.id);
        const wishlisted = isWishlisted(item.id);
        const [hovered, setHovered] = useState(false);

        return (
            <div
                style={{
                    background: hovered ? '#161616' : '#111111',
                    padding: isMobile ? '14px 12px' : '28px 32px',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => setDetailFragrance(item)}
            >
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: isMobile ? '0.55rem' : '0.6rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '4px' }}>{item.brand}</p>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '1.1rem' : '1.4rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '6px', flex: 1, lineHeight: 1.2 }}>{item.name}</p>
                {item.price_gbp && <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '0.9rem' : '1rem', color: '#c9a96e', marginBottom: '8px' }}>£{item.price_gbp}</p>}
                {item.main_accord && <span style={{ display: 'inline-block', fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#665e58', border: '1px solid #222', padding: '2px 6px', marginBottom: isMobile ? '10px' : '18px' }}>{item.main_accord}</span>}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                    {owned ? (
                        <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#665e58', border: '1px solid #222', padding: isMobile ? '4px 8px' : '6px 12px' }}>In Library</span>
                    ) : (
                        <button
                            onClick={e => handleWishlist(e, item)}
                            onMouseEnter={e => { if (!wishlisted) { e.currentTarget.style.background = '#c9a96e'; e.currentTarget.style.color = '#0a0a0a'; } }}
                            onMouseLeave={e => { if (!wishlisted) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#c9a96e'; } }}
                            style={{ background: wishlisted ? '#c9a96e' : 'none', border: `1px solid #c9a96e`, color: wishlisted ? '#0a0a0a' : '#c9a96e', fontFamily: 'Jost, sans-serif', fontSize: '0.55rem', letterSpacing: '0.12em', textTransform: 'uppercase', padding: isMobile ? '4px 8px' : '6px 14px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                        >
                            {wishlisted ? '♥' : '♡ Wishlist'}
                        </button>
                    )}
                    {!isMobile && <span style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#333' }}>View →</span>}
                </div>
            </div>
        );
    };

    return (
        <div style={{ minHeight: '100vh', padding: isMobile ? '88px 0 60px' : '120px 48px 80px', maxWidth: '1200px', margin: '0 auto' }}>

            {/* Header */}
            <div style={{ marginBottom: isMobile ? '24px' : '48px', borderBottom: '1px solid #1e1e1e', paddingBottom: isMobile ? '20px' : '32px', padding: isMobile ? '0 16px 20px' : undefined }}>
                <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '10px' }}>Explore</p>
                <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2rem' : '3rem', fontWeight: 300, color: '#f5f0eb' }}>Discover</h1>
            </div>

            {/* Curated for You */}
            {!hasFilters && recommendations.length > 0 && (
                <div style={{ marginBottom: isMobile ? '32px' : '56px', padding: isMobile ? '0 16px' : undefined }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                        <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e' }}>Curated for You</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {!isMobile && (
                                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '0.85rem', fontStyle: 'italic', color: '#4a4a4a' }}>
                                    {hasNotes ? 'Based on your notes & accords' : 'Based on your accords'}
                                </p>
                            )}
                            {allRecommendations.length > 3 && (
                                <button
                                    onClick={handleRefreshRecs}
                                    style={{ background: 'none', border: '1px solid #2a2a2a', color: '#665e58', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '6px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#c9a96e'; e.currentTarget.style.color = '#c9a96e'; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#665e58'; }}
                                >↻ Refresh</button>
                            )}
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '1px', background: '#1a1a1a' }}>
                        {recommendations.map((item, index) => <FeaturedCard key={item.id} item={item} index={index} />)}
                    </div>
                </div>
            )}

            <div style={{ borderTop: '1px solid #1a1a1a', marginBottom: isMobile ? '0' : '48px' }} />

            {/* Search + Filter row — compact on mobile */}
            <div style={{ padding: isMobile ? '0' : undefined }}>
                <div style={{ display: 'flex', gap: '0', marginBottom: '0' }}>
                    {/* Search input — font-size 16px to prevent Safari zoom */}
                    <input
                        type="text"
                        placeholder="Search by name or brand..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                            flex: 1,
                            background: '#111111',
                            border: '1px solid #2a2a2a',
                            borderRight: isMobile ? 'none' : '1px solid #2a2a2a',
                            borderBottom: 'none',
                            color: '#f5f0eb',
                            fontFamily: 'Jost, sans-serif',
                            fontSize: '16px', // 16px prevents Safari auto-zoom
                            fontWeight: 300,
                            padding: isMobile ? '14px 16px' : '16px 24px',
                            outline: 'none',
                            boxSizing: 'border-box',
                        }}
                    />
                    {/* Filter toggle button on mobile */}
                    {isMobile && (
                        <button
                            onClick={() => setShowFilters(f => !f)}
                            style={{
                                background: activeFilterCount > 0 ? 'rgba(201,169,110,0.1)' : '#111111',
                                border: '1px solid #2a2a2a',
                                borderBottom: 'none',
                                color: activeFilterCount > 0 ? '#c9a96e' : '#665e58',
                                fontFamily: 'Jost, sans-serif',
                                fontSize: '0.6rem',
                                letterSpacing: '0.15em',
                                textTransform: 'uppercase',
                                padding: '14px 16px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                        </button>
                    )}
                </div>

                {/* Filters — always visible on desktop, toggle on mobile */}
                {(!isMobile || showFilters) && (
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', marginBottom: '24px', border: '1px solid #2a2a2a' }}>
                        {[
                            { value: filters.season,      key: 'season',      options: seasons,    placeholder: 'All Seasons'   },
                            { value: filters.occasion,    key: 'occasion',    options: occasions,  placeholder: 'All Occasions' },
                            { value: filters.main_accord, key: 'main_accord', options: allAccords, placeholder: 'All Accords'   },
                            { value: filters.gender,      key: 'gender',      options: allGenders, placeholder: 'All Genders'   },
                        ].map(({ value, key, options, placeholder }, i, arr) => (
                            <select
                                key={key}
                                value={value}
                                onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
                                style={{
                                    flex: 1,
                                    background: '#111111',
                                    border: 'none',
                                    borderBottom: isMobile ? '1px solid #2a2a2a' : 'none',
                                    borderRight: (!isMobile && i < arr.length - 1) ? '1px solid #2a2a2a' : 'none',
                                    color: value ? '#c9a96e' : '#a09890',
                                    fontFamily: 'Jost, sans-serif',
                                    fontSize: '16px', // prevent Safari zoom
                                    letterSpacing: '0.05em',
                                    padding: '12px 16px',
                                    outline: 'none',
                                    cursor: 'pointer',
                                    appearance: 'none',
                                }}
                            >
                                <option value="">{placeholder}</option>
                                {options.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        ))}
                        {hasFilters && (
                            <button
                                onClick={() => { setSearch(''); setFilters({ season: '', occasion: '', main_accord: '', gender: '' }); setShowFilters(false); }}
                                style={{ background: 'none', border: 'none', borderTop: isMobile ? '1px solid #2a2a2a' : 'none', borderLeft: isMobile ? 'none' : '1px solid #2a2a2a', color: '#665e58', fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', padding: '12px 20px', cursor: 'pointer', textAlign: isMobile ? 'left' : 'center' }}
                            >Clear</button>
                        )}
                    </div>
                )}

                {!showFilters && isMobile && <div style={{ height: '1px', background: '#2a2a2a', marginBottom: '24px' }} />}
                {!isMobile && <div style={{ marginBottom: '24px' }} />}
            </div>

            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.6rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '16px', padding: isMobile ? '0 16px' : undefined }}>
                {hasFilters ? `${filtered.length} Result${filtered.length !== 1 ? 's' : ''}` : 'All Fragrances'}
            </p>

            {filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 16px' }}>
                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.5rem', fontWeight: 300, color: '#665e58', fontStyle: 'italic', marginBottom: '16px' }}>No fragrances found</p>
                    {search && (
                        <>
                            <p style={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', fontWeight: 300, color: '#3a3a3a', marginBottom: '24px', lineHeight: 1.7 }}>
                                Can't find what you're looking for?
                            </p>
                            <button
                                onClick={() => setShowRequestModal(true)}
                                style={{ background: 'none', border: '1px solid #2a2a2a', color: '#c9a96e', fontFamily: 'Jost, sans-serif', fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', padding: '12px 28px', cursor: 'pointer' }}
                            >
                                Request this fragrance be added
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: '1px',
                    background: '#2a2a2a',
                    border: isMobile ? 'none' : '1px solid #2a2a2a',
                    borderTop: '1px solid #2a2a2a',
                }}>
                    {filtered.map(item => <FragranceCard key={item.id} item={item} />)}
                </div>
            )}

            {detailFragrance && <ExpandModal item={detailFragrance} token={token} onClose={() => setDetailFragrance(null)} mode="wishlist" />}
            {showRequestModal && (
                <AddFragranceModal
                    token={token}
                    onClose={() => setShowRequestModal(false)}
                    onSuccess={() => setShowRequestModal(false)}
                    preselected={null}
                    defaultView="request"
                    defaultName={search}
                />
            )}
        </div>
    );
};

export default Discover;